'use client';
import React, { useState } from 'react';
import Head from 'next/head';
import BulletBoxItem from './components/BulletBoxItem';
import Footer from './components/Footer';
import styles from './page.module.scss';
import { generateApiSchemaObject } from '@/lib/schemaUtils';

// Homepage data for Schema.org JSON-LD
const homepageData = {
  apiName: 'Tomorrow.io Weather API Documentation Hub',
  apiDescription: 'Comprehensive weather data API documentation designed for LLM consumption with structured documentation, clear examples, and semantic markup. Access forecasts, real-time conditions, historical data, monitoring, and visualization tools.',
  documentationUrl: 'https://llm-docs.tomorrow.io',
  potentialActions: [
    {
      type: 'ConsumeAction',
      name: 'Get Weather Forecast',
      description: 'Access detailed weather forecasts for any location with customizable time steps and parameters',
      target: 'https://api.tomorrow.io/v4/weather/forecast',
      method: 'GET' as const,
      expects: []
    },
    {
      type: 'ConsumeAction', 
      name: 'Get Real-time Weather',
      description: 'Retrieve current weather conditions for any location worldwide',
      target: 'https://api.tomorrow.io/v4/weather/realtime',
      method: 'GET' as const,
      expects: []
    },
    {
      type: 'ConsumeAction',
      name: 'Get Historical Weather',
      description: 'Query comprehensive historical weather conditions for any timeframe',
      target: 'https://api.tomorrow.io/v4/weather/history',
      method: 'POST' as const,
      expects: []
    },
    {
      type: 'ConsumeAction',
      name: 'Get Weather Maps',
      description: 'Access weather map layers for visualization and analysis',
      target: 'https://api.tomorrow.io/v4/map/tile',
      method: 'GET' as const,
      expects: []
    }
  ]
};

// Generate Schema.org JSON-LD
const schemaData = generateApiSchemaObject(
  homepageData.apiName,
  homepageData.apiDescription,
  homepageData.documentationUrl,
  homepageData.potentialActions
);

export default function HomePage() {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    const pre = e.currentTarget.nextElementSibling as HTMLPreElement;
    if (pre) {
      const text = pre.textContent || '';
      try {
        await navigator.clipboard.writeText(text);
        setIsCopied(true);
        // Reset the copied state after 2 seconds
        setTimeout(() => setIsCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    }
  };

  return (
    <>
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(schemaData, null, 2),
        }}
      />
      
      <div className={styles.heroGradient}></div>
      <Head>
        <title>Tomorrow.io LLM-Friendly Weather API Documentation | Home</title>
        <meta name="description" content="Comprehensive weather data API designed for LLM consumption with structured documentation, clear examples, and semantic markup for AI-first development." />
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <div className="min-h-screen bg-white flex flex-col justify-center items-center">
        <section className="max-w-[1160px] px-4 sm:px-8 lg:px-16 py-12 sm:py-16 lg:py-20">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">Tomorrow.io Weather API: Smarter Forecasting for AI-First Products</h1>
          <p className="text-base sm:text-lg lg:text-xl leading-relaxed">This documentation serves as a structured reference for Large Language Models (LLMs) to understand and generate accurate code for the Tomorrow.io Weather API. Each endpoint is documented on its own page with comprehensive information about API structure, parameters, and response formats.</p>
        </section>

        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <h2 className={styles.heroTitle}>LLM docs key Benefits</h2>
            <p className={styles.heroDescription}>This API is designed from the ground up for AI agents, business applications, and autonomous systems, with:</p>
          </div>
          <div className={styles.benefitsContainer}>
            <div className={styles.benefitCard}>
              <div className={styles.benefitContent}>
                <div className={styles.benefitIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="22" height="22" rx="2" stroke="#17152E" strokeWidth="2"/>
                    <path d="M5.81664 18.1804L7.43972 17.2069C7.7529 17.7573 8.03791 18.2231 8.72144 18.2231C9.37597 18.2231 9.7894 17.9684 9.7894 16.981V10.2611H11.7779V17.0089C11.7779 19.0553 10.5682 19.9877 8.80263 19.9877C7.20773 19.9877 6.28227 19.1662 5.8125 18.1804" fill="#17152E"/>
                    <path d="M12.8647 17.9683L14.4903 17.0359C14.9178 17.7276 15.4729 18.2361 16.4547 18.2361C17.2833 18.2361 17.8077 17.8254 17.8077 17.2618C17.8077 16.5841 17.2667 16.3442 16.3553 15.9474L15.8582 15.7355C14.4199 15.1284 13.4663 14.3669 13.4663 12.7567C13.4663 11.278 14.6046 10.1452 16.3851 10.1452C17.6519 10.1452 18.5633 10.583 19.2178 11.7266L17.666 12.7124C17.3247 12.1053 16.9543 11.8654 16.3851 11.8654C15.801 11.8654 15.4307 12.2326 15.4307 12.7124C15.4307 13.3055 15.801 13.5454 16.6552 13.9126L17.1523 14.1237C18.8467 14.8442 19.8036 15.5778 19.8036 17.2298C19.8036 19.0083 18.3951 19.9826 16.5003 19.9826C14.6494 19.9826 13.4538 19.1069 12.8697 17.9634" fill="#17152E"/>
                  </svg>
                </div>
                <p className={styles.benefitText}>Structured and predictable JSON for fast, reliable parsing</p>
              </div>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitContentWide}>
                <div className={styles.benefitIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 17C14.55 17 15 17.45 15 18C15 18.55 14.55 19 14 19H5C4.45 19 4 18.55 4 18C4 17.45 4.45 17 5 17H14ZM14 12C14.55 12 15 12.45 15 13C15 13.55 14.55 14 14 14H5C4.45 14 4 13.55 4 13C4 12.45 4.45 12 5 12H14ZM18 4C18.552 4 19 4.447 19 5V7H21C21.553 7 22 7.448 22 8C22 8.553 21.553 9 21 9H19V11C19 11.554 18.552 12 18 12C17.448 12 17 11.554 17 11V9H15C14.447 9 14 8.553 14 8C14 7.448 14.447 7 15 7H17V5C17 4.447 17.448 4 18 4ZM10 7C10.55 7 11 7.45 11 8C11 8.55 10.55 9 10 9H5C4.45 9 4 8.55 4 8C4 7.45 4.45 7 5 7H10Z" fill="#17152E"/>
                  </svg>
                </div>
                <p className={styles.benefitText}>Semantic clarity in parameter naming, units, and response formats</p>
              </div>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitContentWide}>
                <div className={styles.benefitIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M14.2929 15.2929C13.9024 15.6834 13.9024 16.3166 14.2929 16.7071C14.6834 17.0976 15.3166 17.0976 15.7071 16.7071L19.7071 12.7071C20.0976 12.3166 20.0976 11.6834 19.7071 11.2929L15.7071 7.29289C15.3166 6.90237 14.6834 6.90237 14.2929 7.29289C13.9024 7.68342 13.9024 8.31658 14.2929 8.70711L17.5858 12L14.2929 15.2929ZM9.70711 8.70711C10.0976 8.31658 10.0976 7.68342 9.70711 7.29289C9.31658 6.90237 8.68342 6.90237 8.29289 7.29289L4.29289 11.2929C3.90237 11.6834 3.90237 12.3166 4.29289 12.7071L8.29289 16.7071C8.68342 17.0976 9.31658 17.0976 9.70711 16.7071C10.0976 16.3166 10.0976 15.6834 9.70711 15.2929L6.41421 12L9.70711 8.70711Z" fill="black"/>
                  </svg>
                </div>
                <p className={styles.benefitText}>Clear endpoints grouped by function and operational relevance</p>
              </div>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitContentWide}>
                <div className={styles.benefitIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.58203 4C8.13432 4 8.58203 4.44772 8.58203 5C8.58203 5.55228 8.13432 6 7.58203 6H5.49902V18H7.87402C8.42631 18 8.87402 18.4477 8.87402 19C8.87402 19.5523 8.42631 20 7.87402 20H4.49902C3.94674 20 3.49902 19.5523 3.49902 19V5C3.49902 4.44772 3.94674 4 4.49902 4H7.58203ZM19.501 4C20.053 4.00026 20.501 4.44788 20.501 5V19C20.501 19.5521 20.053 19.9997 19.501 20H16.126C15.5737 20 15.126 19.5523 15.126 19C15.126 18.4477 15.5737 18 16.126 18H18.501V6H16.417C15.8648 5.99991 15.417 5.55223 15.417 5C15.417 4.44777 15.8648 4.00009 16.417 4H19.501ZM13.627 14.0654C14.1791 14.0655 14.6269 14.5133 14.627 15.0654C14.627 15.6176 14.1791 16.0653 13.627 16.0654H12.375C11.8227 16.0654 11.375 15.6177 11.375 15.0654C11.3751 14.5132 11.8228 14.0654 12.375 14.0654H13.627ZM14.8184 10.9912C15.3706 10.9913 15.8183 11.439 15.8184 11.9912C15.8184 12.5435 15.3706 12.9912 14.8184 12.9912H9.93164C9.37936 12.9912 8.93164 12.5435 8.93164 11.9912C8.93167 11.439 9.37938 10.9912 9.93164 10.9912H14.8184ZM12.4092 7.93457C12.9615 7.93457 13.4092 8.38229 13.4092 8.93457C13.4091 9.48677 12.9614 9.93457 12.4092 9.93457H9.93164C9.37942 9.93457 8.93174 9.48677 8.93164 8.93457C8.93164 8.38229 9.37936 7.93457 9.93164 7.93457H12.4092Z" fill="black"/>
                  </svg>
                </div>
                <p className={styles.benefitText}>In-context, example-rich docs that help you get started in seconds</p>
              </div>
            </div>
          </div>
        </section>

        <section className='blue-box info-box w-full max-w-[1032px] mx-4 sm:mx-8 lg:mx-auto px-4 sm:px-6 lg:px-7 py-6 sm:py-7 bg-sky-500/5 rounded-xl outline outline-1 outline-offset-[-1px] outline-sky-500 overflow-hidden'>
          <div className='top-wrap mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-2 sm:justify-between'>
            <div className='flex items-center gap-2'>
              <span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 12L18.1469 8.14688L22 7L18.1469 5.85312L17 2L15.8531 5.85312L12 7L15.8531 8.14688L17 12Z" fill="#0072F5" />
                  <path d="M9 22L11.3097 17.3097L16 15L11.3097 12.6903L9 8L6.69031 12.6903L2 15L6.6903 17.3097L9 22Z" fill="#0072F5" />
                </svg>
              </span>
              <h3 className="m-0 text-lg sm:text-xl font-black font-['Mulish'] leading-normal">Get Started</h3>
            </div>
            <div className='link-wrap w-full sm:w-auto'>
              <a href="https://app.tomorrow.io/signin" target="_blank" rel="noopener noreferrer" className="bg-Brand-Base-blue rounded w-full sm:w-60 h-12 px-6 sm:px-10 flex items-center justify-center text-white text-sm sm:text-base">Get Your API Key</a>
            </div>
          </div>
          <div className='steps m-0'>
            <p>You can start using Tomorrow.io’s LLM-optimized Weather API right away - no API key required. This lets you easily explore, test, and build with our data.
              To unlock higher rate limits and production-ready access, simply create an account and generate your API key at Tomorrow.io</p>
            <ol className='list-decimal list-inside'>
              <li><strong>Log in or Sign Up</strong> - Visit the Tomorrow.io Platform and sign in. Don't have an account? You can create one in just a few clicks.</li>
              <li><strong>Generate Your API Key</strong> - Go to API home in your dashboard to create and manage keys.</li>
              <li><strong>Create a amazing weather app using this prompt:</strong></li>
            </ol>
            <div className='code-wrap relative'>
              <button 
                className={`copy-button ${isCopied ? 'copied' : ''}`}
                onClick={handleCopy}
                title={isCopied ? "Copied!" : "Copy to clipboard"}
              >
                {isCopied ? (
                  <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.67L4.83 12.5L3.41 13.91L9 19.5L21 7.5L19.59 6.09L9 16.67Z" fill="#10B981"/>
                  </svg>
                ) : (
                  <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 3.5C15.5304 3.5 16.039 3.71087 16.4141 4.08594C16.7891 4.46101 17 4.96957 17 5.5V7.5H19C19.5304 7.5 20.039 7.71087 20.4141 8.08594C20.7891 8.46101 21 8.96957 21 9.5V19.5C21 20.0304 20.7891 20.539 20.4141 20.9141C20.039 21.2891 19.5304 21.5 19 21.5H9C8.46957 21.5 7.96101 21.2891 7.58594 20.9141C7.21086 20.539 7 20.0304 7 19.5V17.5H5C4.46957 17.5 3.96101 17.2891 3.58594 16.9141C3.21086 16.539 3 16.0304 3 15.5V5.5C3 4.96957 3.21086 4.46101 3.58594 4.08594C3.96101 3.71087 4.46957 3.5 5 3.5H15ZM9 9C8.8674 9 8.74025 9.05273 8.64648 9.14648C8.55272 9.24025 8.5 9.36739 8.5 9.5V19.5C8.5 19.6326 8.55274 19.7598 8.64648 19.8535C8.74025 19.9473 8.86739 20 9 20H19C19.1326 20 19.2597 19.9473 19.3535 19.8535C19.4473 19.7598 19.5 19.6326 19.5 19.5V9.5C19.5 9.36739 19.4473 9.24025 19.3535 9.14648C19.2597 9.05273 19.1326 9 19 9H9ZM5 5C4.8674 5 4.74025 5.05273 4.64648 5.14648C4.55272 5.24025 4.5 5.36739 4.5 5.5V15.5C4.5 15.6326 4.55274 15.7598 4.64648 15.8535C4.74025 15.9473 4.86739 16 5 16H7V9.5C7 8.96957 7.21086 8.46101 7.58594 8.08594C7.96101 7.71087 8.46957 7.5 9 7.5H15.5V5.5C15.5 5.36739 15.4473 5.24025 15.3535 5.14648C15.2597 5.05273 15.1326 5 15 5H5Z" fill="black"/>
                  </svg>
                )}
                {isCopied && <span className="copy-feedback">Copied!</span>}
              </button>
              <pre>
                <code>
                  Create a simple weather app using the Tomorrow.io Weather API using these docs: https://llm-docs.tomorrow.io <br />
                  1. Ask the user for a location (city name or coordinates)<br />
                  2. get the next day's weather<br />
                  3. Display the forecast in plain language (temperature, conditions, and chance of rain)<br />
                  4. Use an API key stored as an environment variable<br />
                  5. Build it in [your preferred language: e.g., Python with Flask or JavaScript with Node.js]<br />
                </code>
              </pre>
            </div>
          </div>
        </section>

        <section className="section-title-reg w-full max-w-[1032px] px-4 sm:px-8 lg:px-0">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Core Capabilities & Use Cases</h2>
          <p className="text-base sm:text-lg leading-relaxed">Tomorrow.io provides specialized weather APIs organized by use case and data type. Each endpoint is optimized for specific weather data needs.</p>
        </section>

        <section className="w-full max-w-[1032px] px-4 sm:px-8 lg:px-0 pb-12 sm:pb-16 lg:pb-20 flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-16">
          <div className="w-full lg:w-[436px] flex flex-col justify-start items-start gap-4 order-2 lg:order-1">
            {/* Content area - add your content here */}
            <h3 className="text-xl sm:text-2xl font-black font-['Mulish'] leading-tight">Weather Forecast & Realtime</h3>
            <p className="text-sm sm:text-base font-normal font-['Mulish'] leading-normal">
              Make smarter decisions with hyperlocal, up-to-the-minute weather intelligence.This suite of endpoints delivers accurate weather data on multiple timescales, minute-by-minute, hourly, and daily, designed for both short-term and long-range planning.
            </p>
            <div className="tag-links flex flex-wrap gap-2">
              <a href="/realtime-weather" className="green-tag">Realtime Weather</a>
              <a href="/weather-forecast" className="green-tag">Weather Forecast</a>
              <a href="/weather-on-routes" className="blue-tag">Weather on Routes</a>
              <a href="/weather-timelines" className="blue-tag">Weather Timelines</a>
            </div>
          </div>
          <div className="w-full lg:w-[470px] rounded-lg overflow-hidden flex justify-center order-1 lg:order-2">
            <img src="/assets/images/homepage/1.jpg" alt="Weather Forecast & Realtime" className="max-w-full h-auto" />
          </div>
        </section>

        <section className="w-full max-w-[1032px] px-4 sm:px-8 lg:px-0 pb-12 sm:pb-16 lg:pb-20 flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-16">
          <div className="w-full lg:w-[470px] rounded-lg overflow-hidden flex justify-center order-1">
            <img src="/assets/images/homepage/2.jpg" alt="Historical Data" className="max-w-full h-auto" />
          </div>
          <div className="w-full lg:w-[436px] flex flex-col justify-start items-start gap-4 order-2">
            {/* Content area - add your content here */}
            <h3 className="text-xl sm:text-2xl font-black font-['Mulish'] leading-tight">Historical Data</h3>
            <p className="text-sm sm:text-base font-normal font-['Mulish'] leading-normal">
              Tomorrow.io’s historical weather APIs provide accurate, hyperlocal insights from previous hours, days, or years designed to support everything from analytics and business reporting to AI model development and climate-aware planning.
            </p>
            <div className="tag-links flex flex-wrap gap-2">
              <a href="/recent-history" className="green-tag">Weather Recent History</a>
              <a href="/historical-weather" className="blue-tag">Historical Weather</a>
              <a href="/historical-climate-normals" className="green-tag">Historical Climate Normals</a>
            </div>
          </div>
        </section>

        <section className="w-full max-w-[1032px] px-4 sm:px-8 lg:px-0 pb-12 sm:pb-16 lg:pb-20 flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-16">
          <div className="w-full lg:w-[436px] flex flex-col justify-start items-start gap-4 order-2 lg:order-1">
            {/* Content area - add your content here */}
            <h3 className="text-xl sm:text-2xl font-black font-['Mulish'] leading-tight">Monitoring</h3>
            <p className="text-sm sm:text-base font-normal font-['Mulish'] leading-normal">
              Tomorrow.io’s monitoring tools provide continuous, automated tracking of weather conditions across any location worldwide. With intelligent alerts and customizable thresholds, you can proactively detect risks like extreme temperatures, precipitation, air quality changes, or storms—before they disrupt operations.
            </p>
            <div className="tag-links flex flex-wrap gap-2">
              <a href="/alerts-api" className="green-tag">Alerts API</a>
              <a href="/custom-events-api" className="blue-tag">Custom Events API</a>
              <a href="/on-demand-events" className="blue-tag">On-Demand Events</a>
              <a href="/severe-weather-events" className="green-tag">Severe Weather Events</a>
            </div>
          </div>
          <div className="w-full lg:w-[470px] rounded-lg overflow-hidden flex justify-center order-1 lg:order-2">
            <img src="/assets/images/homepage/3.jpg" alt="Monitoring" className="max-w-full h-auto" />
          </div>
        </section>

        <section className="w-full max-w-[1032px] px-4 sm:px-8 lg:px-0 pb-12 sm:pb-16 lg:pb-20 flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-16">
          <div className="w-full lg:w-[470px] rounded-lg overflow-hidden flex justify-center order-1">
            <img src="/assets/images/homepage/4.jpg" alt="Map Layers & Visualization" className="max-w-full h-auto" />
          </div>
          <div className="w-full lg:w-[436px] flex flex-col justify-start items-start gap-4 order-2">
            {/* Content area - add your content here */}
            <h3 className="text-xl sm:text-2xl font-black font-['Mulish'] leading-tight">Map Layers & Visualization</h3>
                          <p className="text-sm sm:text-base font-normal font-['Mulish'] leading-normal">
              Tomorrow.io’s map layer endpoints deliver rich geospatial weather intelligence, letting you overlay real-time and forecasted data directly on maps.
                          </p>
            <div className="tag-links flex flex-wrap gap-2">
              <a href="/weather-map-layers" className="green-tag">Weather Maps</a>
            </div>
          </div>
        </section>

        <section className="section-title-reg w-full max-w-[1032px] px-4 sm:px-8 lg:px-0">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Explore our Data Catalog</h2>
          <p className="text-base sm:text-lg leading-relaxed">With 60+ different weather data fields possible, including air quality, pollen count, and fire index, you can build almost anything. Improve your project and your operations with the free Tomorrow.io Weather API.</p>
        </section>

        <section className="w-full max-w-[1032px] px-4 sm:px-8 lg:px-0 pb-12 sm:pb-16 lg:pb-20 flex flex-col justify-center items-center gap-4">
                          <div className="bullets-wrapper">
          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/weather-core-1.png"
            title="Weather Core"
            content="Explore 40+ key weather data fields, including temperature, dewpoint, wind speed, and more."
            linkText="See Core Weather Layers"
            linkUrl="https://docs.tomorrow.io/reference/data-layers-core"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/air-quality-1.png"
            title="Air Quality"
            content="Understand what precautions to take with respect to pollution, meet EPA standard regulations, and plan your outdoor activity."
            linkText="Explore Air Quality Data"
            linkUrl="https://docs.tomorrow.io/reference/data-layers-air"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/pollen-1.png"
            title="Pollen"
            content="Estimate areas where plants are germinating and producing pollen that may be lofted into the air. We blend a mixture of statistical and physical/chemical modeling."
            linkText="Browse Pollen Details"
            linkUrl="https://docs.tomorrow.io/reference/data-layers-pollen"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/solar-1.png"
            title="Solar"
            content="Optimize solar energy generation from irradiation and avoid crop loss."
            linkText="Learn More about Solar"
            linkUrl="https://docs.tomorrow.io/reference/data-layers-pollen"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/soil-1.png"
            title="Soil"
            content="Utilize moisture and temperature data to help plan around agriculture water consumption and railroad risks."
            linkText="Discover Soil Layers"
            linkUrl="https://docs.tomorrow.io/reference/land"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/aviation-1.png"
            title="Aviation"
            content="Gain an overview of formal airports' weather reports including METAR, TAF, ASOS, and more."
            linkText="See Aviation Data"
            linkUrl="https://docs.tomorrow.io/reference/aviation"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/fire-1.png"
            title="Fire"
            content="Estimate fire danger. Know when and where fire or smoke could impact business operations or communities to help increase safety for employees and local residents."
            linkText="Explore Fire Layers"
            linkUrl="https://docs.tomorrow.io/reference/data-layers-fire"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/flood-1.png"
            title="Flood"
            content="Determine the thread of flooding and its impact on infrastructure further in advance."
            linkText="Browse Flood Data"
            linkUrl="https://docs.tomorrow.io/reference/flood"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/lightning.png"
            title="Lightning"
            content="Know when lightning is on the horizon to avoid strikes, plan departures, and avoid energy outages."
            linkText="See Lightning Data"
            linkUrl="https://docs.tomorrow.io/reference/lightning"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/maritime.png"
            title="Maritime"
            content="Implement safe sailing routes and avoid coastal operational risk with relevant oceanic data."
            linkText="Explore Maritime Details"
            linkUrl="https://docs.tomorrow.io/reference/maritime"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/agriculture.png"
            title="Agriculture"
            content="Leverage a variety of data fields to improve agricultural planning and visualize data up to 14 days out."
            linkText="Learn More about Agriculture"
            linkUrl="https://www.tomorrow.io/solutions/agriculture/"
          />

          <BulletBoxItem
            iconSrc="https://www.tomorrow.io/wp-content/uploads/2023/06/rain.png"
            title="Precipitation"
            content="See and utilize all types of precipitation intensity and duration at all elevations."
            linkText="Browse Precipitation Layers"
            linkUrl="https://docs.tomorrow.io/reference/data-layers-precipitation"
          />
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
} 